/**
 * MamaLink - Contexte de thème (Dark Mode)
 * Une petite attention pour quand maman allaite la nuit
 * Avec persistance en base de données
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ThemeContext = createContext();
const API = process.env.REACT_APP_BACKEND_URL;

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('mamalink-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });

  const [silentNotifications, setSilentNotifications] = useState(() => {
    const saved = localStorage.getItem('mamalink-silent-notifications');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Accessibility settings
  const [highContrast, setHighContrast] = useState(() => {
    const saved = localStorage.getItem('mamalink-high-contrast');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [largeText, setLargeText] = useState(() => {
    const saved = localStorage.getItem('mamalink-large-text');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [isSyncing, setIsSyncing] = useState(false);

  // Sync preferences to backend
  const syncPreferencesToBackend = useCallback(async (darkMode, silent) => {
    const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
    if (!token) return;
    
    setIsSyncing(true);
    try {
      await axios.post(`${API}/api/notifications/preferences`, {
        dark_mode: darkMode,
        silent_notifications: silent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error syncing preferences:', error);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Load preferences from backend on mount
  useEffect(() => {
    const loadPreferencesFromBackend = async () => {
      const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/api/notifications/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          // Update local state if backend has different values
          if (response.data.dark_mode !== undefined) {
            setIsDarkMode(response.data.dark_mode);
            localStorage.setItem('mamalink-dark-mode', JSON.stringify(response.data.dark_mode));
          }
          if (response.data.silent_notifications !== undefined) {
            setSilentNotifications(response.data.silent_notifications);
            localStorage.setItem('mamalink-silent-notifications', JSON.stringify(response.data.silent_notifications));
          }
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    };
    
    loadPreferencesFromBackend();
  }, []);

  useEffect(() => {
    localStorage.setItem('mamalink-dark-mode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('mamalink-silent-notifications', JSON.stringify(silentNotifications));
  }, [silentNotifications]);

  // Apply high contrast mode
  useEffect(() => {
    localStorage.setItem('mamalink-high-contrast', JSON.stringify(highContrast));
    if (highContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  }, [highContrast]);

  // Apply large text mode
  useEffect(() => {
    localStorage.setItem('mamalink-large-text', JSON.stringify(largeText));
    if (largeText) {
      document.documentElement.classList.add('large-text');
    } else {
      document.documentElement.classList.remove('large-text');
    }
  }, [largeText]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      syncPreferencesToBackend(newValue, silentNotifications);
      return newValue;
    });
  };
  
  const toggleSilentNotifications = () => {
    setSilentNotifications(prev => {
      const newValue = !prev;
      syncPreferencesToBackend(isDarkMode, newValue);
      return newValue;
    });
  };

  const toggleHighContrast = () => {
    setHighContrast(prev => !prev);
  };

  const toggleLargeText = () => {
    setLargeText(prev => !prev);
  };

  return (
    <ThemeContext.Provider value={{
      isDarkMode,
      toggleDarkMode,
      silentNotifications,
      toggleSilentNotifications,
      highContrast,
      toggleHighContrast,
      largeText,
      toggleLargeText,
      isSyncing
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
